import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../../shared/shared-imports';
import { ApiService } from '../../../../../core/services/api.service';
import { SharedService } from '../../../../../shared/services/shared.service';
import { ApiEndPoints, ApiRoot } from '../../../../../shared/constants/api-endpoints';
import { LookupKey } from '../../../../../shared/constants/lookup-keys';
import { CooPayload } from '../../../../../shared/models/services-request/issue-new-coo.model';
import { HSCodeRequest } from '../../../../../shared/models/hs-code-request.model';
import { Utils } from '../../../../../Utils/utils';
import { FieldValue, UserType } from '../../../../../shared/constants/services.enum';
import { RoutesPath } from '../../../../../shared/constants/routes-path';

interface UploadResult {
  index: number;
  status: 'success' | 'validation_error' | 'api_error';
  message: string;
  srNumber?: string;
}

@Component({
  selector: 'app-coo-bulk-upload',
  imports: [CommonModule, TranslateModule, ...SHARED_COMPONENTS],
  templateUrl: './coo-bulk-upload.component.html',
  styleUrl: './coo-bulk-upload.component.scss'
})
export class CooBulkUploadComponent implements OnInit {
  readonly maxFileSizeBytes = 20 * 1024 * 1024;
  selectedFileName = '';
  processing = false;
  file: File[] = [];
  memberships: any[] = [];
  userType: string = '';

  validationErrors: string[] = [];
  uploadResults: UploadResult[] = [];
  isMasterDataLoading = false;

  private readonly lovCache = new Map<string, Set<string>>();
  private readonly countryCache = new Map<string, Set<string>>();
  private readonly masterHsCodeSet = new Set<string>();
  private readonly masterHsCodeMetaMap = new Map<string, { englishName: string; arabicName: string }>();
  private readonly exporterNumberCache = new Map<string, Set<string>>();
  private hsCodeRequest: HSCodeRequest = new HSCodeRequest();
  private preloadMastersPromise: Promise<void> | null = null;

  constructor(
    private api: ApiService,
    private sharedService: SharedService,
    private translateService: TranslateService,
    private router: Router
  ) {

    this.userType = this.sharedService.getPrimaryAccount()?.['Account Type Code'];
  }

  ngOnInit(): void {
    //only for Member
    if (this.userType != UserType.MEMBER) {
      this.sharedService.toastMessage('INFO', 'ONLY_MEMBER_ALLOWED', 'info', true);
      this.router.navigate([RoutesPath.DASHBOARD]);
      return;
    }

    this.getActiveMembership();
    // check for Professional / Service License
    let account = this.sharedService.getPrimaryAccount();
    if (!this.validateLicenseEligibility(account)) {
      return;
    }

    if (this.memberships.length < 1) {
      this.sharedService.toastMessage('INFO', 'NO_ACTIVE_MEMBERSHIP', 'info', true);
      this.router.navigate([RoutesPath.DASHBOARD]);
      return;
    }

    this.preloadMastersPromise = this.preloadValidationMasters();
  }

  // This method is used to get active membership
  private getActiveMembership() {
    this.userType = this.sharedService.getPrimaryAccount()?.['Account Type Code'] ?? '';
    const loginUserDetail = this.sharedService.getUserProfile();
    const accounts: any[] = Utils.checkIsArray(loginUserDetail?.Account);
    this.memberships = accounts.filter((acc: any) => {
      if (!acc.CSN || acc.CSN.trim() === '') {
        return false;
      }
      if (this.sharedService.isInactiveMemberAccount(acc)) {
        return false;
      }
      const accountLicenseType = acc['DC License Type'];
      if (
        accountLicenseType === FieldValue.OFFSHORE_LICENSE ||
        accountLicenseType === FieldValue.OFFSHORE_LICENSE_AR
      ) {
        return false;
      }
      return Utils.isMembershipValid(acc['DC Expiry Date']);
    });
  }

  private validateLicenseEligibility(account: any): boolean {
    const memberActivities = this.sharedService.getPrimaryMemberActivities() || [];
    const professionalLicenseAllowedActivityCodes = ['5210002', 'I630203', 'G515101'];
    const accountLicenseType = account?.['DC License Type'];
    if (accountLicenseType === FieldValue.PROFESSIONAL_LICENSE) {
      const exceptionFlag = account?.['DC COO Exception'];
      if (exceptionFlag === 'Y') {
        return true;
      }
      const hasAllowedActivityCode = memberActivities.some((activity: any) => {
        const activityCode = (activity?.['DC Activity Code'] || '').toString().trim().toUpperCase();
        return professionalLicenseAllowedActivityCodes.includes(activityCode);
      });
      if (hasAllowedActivityCode) {
        return true;
      }
      this.sharedService.toastMessage(
        'INFO',
        'PROFESSIONAL_SERVICE_LICENSE_NOTALLOWED',
        'info',
        true
      );
      this.router.navigate([RoutesPath.DASHBOARD]);
      return false;
    }
    return true;
  }

  async onFileChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.validationErrors = [];
    this.uploadResults = [];

    if (!file) {
      this.selectedFileName = '';
      this.file = [];
      return;
    }

    this.file = [file];
    this.selectedFileName = file.name;

    if (!file.name.toLowerCase().endsWith('.json')) {
      this.validationErrors = [this.t('COO_BULK_UPLOAD.ERRORS.INVALID_FILE_TYPE')];
      input.value = '';
      this.file = [];
      return;
    }

    if (file.size > this.maxFileSizeBytes) {
      this.validationErrors = [this.t('COO_BULK_UPLOAD.ERRORS.MAX_FILE_SIZE')];
      input.value = '';
      this.file = [];
      return;
    }

    try {
      this.processing = true;

      const mastersReady = await this.ensureMasterDataReady();
      if (!mastersReady) {
        this.validationErrors = [this.t('COO_BULK_UPLOAD.ERRORS.API_TECHNICAL')];
        return;
      }

      const raw = await file.text();
      const parsed = JSON.parse(raw);
      const records = this.extractServiceReqList(parsed);

      if (!records.length) {
        this.validationErrors = [this.t('COO_BULK_UPLOAD.ERRORS.NO_SERVICEREQ')];
        return;
      }

      const csnCheck = this.validatePrimaryMemberCsn(records);
      if (!csnCheck.valid) {
        this.validationErrors = [csnCheck.message];
        return;
      }

      await this.processRecords(records);
    } catch {
      this.validationErrors = [this.t('COO_BULK_UPLOAD.ERRORS.INVALID_JSON')];
    } finally {
      this.processing = false;
      // Always clear chooser so user can re-upload the same file again.
      input.value = '';
      this.file = [];
      this.selectedFileName = '';
    }
  }

  private validatePrimaryMemberCsn(records: any[]): { valid: boolean; message: string } {
    const selectedPrimaryCsn = (this.sharedService.getCsnAccountId('csn') || '').toString().trim();
    if (!selectedPrimaryCsn) {
      return {
        valid: false,
        message: 'Primary member CSN is not selected. Please select a primary member and try again.'
      };
    }

    const invalidRecordIndex = records.findIndex((record: any) => {
      const recordCsn = (record?.CSN || '').toString().trim();
      return !recordCsn || recordCsn !== selectedPrimaryCsn;
    });

    if (invalidRecordIndex !== -1) {
      const uploadedCsn = (records[invalidRecordIndex]?.CSN || '').toString().trim() || '-';
      return {
        valid: false,
        message: `CSN number should be for selected primary member. Selected primary CSN: ${selectedPrimaryCsn}, object ${invalidRecordIndex + 1} CSN: ${uploadedCsn}.`
      };
    }

    return { valid: true, message: '' };
  }

  remove(): void {
    this.file = [];
    this.selectedFileName = '';
    this.validationErrors = [];
    this.uploadResults = [];
  }

  downloadTemplateFile(type: string): void {
    if (type === 'star-new') {
      return;
    }
    const link = document.createElement('a');
    link.href = this.templateUrl;
    link.click();
  }

  private extractServiceReqList(payload: any): any[] {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.SERVICEREQ)) {
      return payload.SERVICEREQ;
    }

    if (Array.isArray(payload?.body?.DCIncomingHierarchy?.ListOfDCCOOMOBILE?.MEMBER?.ListOfSERVICEREQ?.SERVICEREQ)) {
      return payload.body.DCIncomingHierarchy.ListOfDCCOOMOBILE.MEMBER.ListOfSERVICEREQ.SERVICEREQ;
    }

    if (payload?.body?.DCIncomingHierarchy?.ListOfDCCOOMOBILE?.MEMBER?.ListOfSERVICEREQ?.SERVICEREQ) {
      return [payload.body.DCIncomingHierarchy.ListOfDCCOOMOBILE.MEMBER.ListOfSERVICEREQ.SERVICEREQ];
    }

    return [];
  }

  private async processRecords(records: any[]): Promise<void> {
    if (this.masterHsCodeSet.size === 0) {
      await this.preloadMasterHsCodes();
    }

    for (let i = 0; i < records.length; i++) {
      const index = i + 1;
      const record = records[i] ?? {};
      const recordErrors = await this.validateRecord(record, index);

      if (recordErrors.length) {
        this.uploadResults.push({
          index,
          status: 'validation_error',
          message: this.buildInvalidFieldMessage(recordErrors)
        });
        continue;
      }

      const invoiceValidation = await this.validateInvoiceNumber(record, index);
      if (!invoiceValidation.valid) {
        this.uploadResults.push({
          index,
          status: 'validation_error',
          message: invoiceValidation.message
        });
        continue;
      }

      const payload = this.buildCreatePayload(record);

      try {
        const response: any = await firstValueFrom(
          this.api.postWithoutRetry(ApiRoot.DCCICPIntegration, ApiEndPoints.CREATE_COO, payload)
        );

        if ((!response?.['Error Code'] || response?.['Error Code'] === '') && (!response?.['Error Message'] || response?.['Error Message'] === '')) {
          this.uploadResults.push({
            index,
            status: 'success',
            message: this.t('COO_BULK_UPLOAD.RESULT.SUBMITTED_SUCCESS'),
            srNumber: response?.DCSRNumber || ''
          });
        } else {
          this.uploadResults.push({
            index,
            status: 'api_error',
            message: response?.['Error Message'] || response?.['Error Code'] || this.t('COO_BULK_UPLOAD.ERRORS.API_CREATE')
          });
        }
      } catch {
        this.uploadResults.push({
          index,
          status: 'api_error',
          message: this.t('COO_BULK_UPLOAD.ERRORS.API_TECHNICAL')
        });
      }
    }
  }

  private async preloadValidationMasters(): Promise<void> {
    this.isMasterDataLoading = true;
    try {
      await Promise.all([
        this.isValidCurrency('AED'),
        this.isValidLov(LookupKey.COO_DC_EXP_TYPE, 'Consigner'),
        this.isValidLov(LookupKey.COO_DC_CONSGN_TYPE, 'Consignee'),
        this.isValidLov(LookupKey.COO_DC_TRASNPORT, 'By Air'),
        this.isValidLov(LookupKey.COO_DC_COO_EXIT_POINT, 'JEBEL ALI POST OFFICE'),
        this.isValidLov(LookupKey.COUNTRY, 'India'),
        this.isValidLov(LookupKey.PAYMENT_METHOD, 'Online'),
        this.isValidLov(LookupKey.COO_DC_LANG, 'Arabic'),
        this.isValidLov(LookupKey.COO_DC_PROCESS_TYPE, 'Assembled In'),
        this.isValidLov(LookupKey.UNIT_OF_MEASURE, 'Bag'),
        this.preloadMasterHsCodes()
      ]);
    } finally {
      this.isMasterDataLoading = false;
    }
  }

  private async ensureMasterDataReady(): Promise<boolean> {
    try {
      if (!this.preloadMastersPromise) {
        this.preloadMastersPromise = this.preloadValidationMasters();
      }

      await this.preloadMastersPromise;

      if (this.masterHsCodeSet.size === 0) {
        this.preloadMastersPromise = this.preloadValidationMasters();
        await this.preloadMastersPromise;
      }

      return this.masterHsCodeSet.size > 0;
    } catch {
      return false;
    }
  }

  private async preloadMasterHsCodes(): Promise<void> {
    const pageSize = 7000;
    let startRowNum = 0;
    let totalRecords = 0;

    this.masterHsCodeSet.clear();
    this.masterHsCodeMetaMap.clear();
    const product = this.hsCodeRequest.body.IncomingHierarchy.ListOfDCHSCODELIST.PRODUCT;
    product.ProductCode = '';
    product.Description = '';

    do {
      this.hsCodeRequest.body.IncomingHierarchy.MessageId = Utils.generateUniqueId();
      this.hsCodeRequest.body.StartRowNum = startRowNum;
      this.hsCodeRequest.body.PageSize = `${pageSize}`;
      this.hsCodeRequest.body.NewQuery = startRowNum === 0 ? 'true' : 'false';
      this.hsCodeRequest.body.DCLanguage = this.sharedService.getCurrentLang() === 'ar' ? 'ARA' : 'ENU';

      const response: any = await firstValueFrom(
        this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_HS_CODE, this.hsCodeRequest)
      );

      const hsProducts = Utils.checkIsArray(response?.SiebelMessage?.['PRODUCT']);
      totalRecords = Number(response?.TotalRecords || 0);
      hsProducts.forEach((hs: any) => {
        const code = (hs?.ProductCode ?? '').toString().trim();
        if (code) {
          const key = code.toLowerCase();
          this.masterHsCodeSet.add(key);
          this.masterHsCodeMetaMap.set(key, {
            englishName: (hs?.Description ?? '').toString().trim(),
            arabicName: (hs?.ProductNameARA ?? '').toString().trim()
          });
        }
      });

      startRowNum += pageSize;
    } while (startRowNum < totalRecords);
  }

  private async validateInvoiceNumber(record: any, index: number): Promise<{ valid: boolean; message: string }> {
    const memberNo = (record?.CSN ?? '').toString().trim();
    const invoiceNo = (record?.['DC Invoice Number'] ?? '').toString().trim();
    const invoiceDate = (record?.['DC Invoice Date'] ?? '').toString().trim();

    if (!memberNo || !invoiceNo || !invoiceDate) {
      return {
        valid: false,
        message: this.t('COO_BULK_UPLOAD.ERRORS.INVOICE_VALIDATION_REQUIRED', { index })
      };
    }

    const requestPayload = {
      body: {
        ProcessName: 'DC Check Duplicate COO Invoice Number WF',
        vMemNum: memberNo,
        vInvDate: invoiceDate,
        vInvNum: invoiceNo
      }
    };

    try {
      const response: any = await firstValueFrom(
        this.api.post(
          ApiRoot.DCCICPIntegration,
          ApiEndPoints.VALIDATE_COO_INVOICE_NUMBER,
          requestPayload
        )
      );

      const hasErrorCode = !!response?.['Error Code'];
      const hasErrorMessage = !!response?.['Error Message'];
      if (hasErrorCode || hasErrorMessage) {
        return {
          valid: false,
          message: this.t('COO_BULK_UPLOAD.ERRORS.DUPLICATE_INVOICE', { index, invoiceNo })
        };
      }

      return { valid: true, message: '' };
    } catch {
      return {
        valid: false,
        message: this.t('COO_BULK_UPLOAD.ERRORS.INVOICE_VALIDATION_FAILED', { index })
      };
    }
  }

  private buildCreatePayload(serviceReq: any): CooPayload {
    const request = new CooPayload();
    const normalizedServiceReq = { ...(serviceReq || {}) };
    request.body.ProcessName = 'DC Community Portal SR Process Wrapper WF';
    request.body.DCMethod = 'CREATE';
    request.body.DCLanguage = this.sharedService.getCurrentLang() === 'ar' ? 'ARA' : 'ENU';
    request.body.DCLoginName = this.sharedService.getUserProfile()?.['Login Name'] || 'JOEROOT';

    request.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    request.body.DCIncomingHierarchy.IntObjectName = 'DCCOOMOBILE';
    request.body.DCIncomingHierarchy.MessageType = 'Integration Object';
    request.body.DCIncomingHierarchy.IntObjectFormat = 'Siebel Hierarchical';

    normalizedServiceReq['DC No of COO Sets'] = `${normalizedServiceReq['DC No of COO Sets'] ?? '1'}`;
    normalizedServiceReq['DC No of Copies'] = `${normalizedServiceReq['DC No of Copies'] ?? '1'}`;
    normalizedServiceReq['DC No of Invoice Pages'] = `${normalizedServiceReq['DC No of Invoice Pages'] ?? '1'}`;
    normalizedServiceReq['DC No of Invoice Sets'] = `${normalizedServiceReq['DC No of Invoice Sets'] ?? '1'}`;
    normalizedServiceReq['DC No of Pack List Pages'] = `${normalizedServiceReq['DC No of Pack List Pages'] ?? '1'}`;
    normalizedServiceReq['DC No of Pack List Sets'] = `${normalizedServiceReq['DC No of Pack List Sets'] ?? '1'}`;
    normalizedServiceReq['DC Doc AI Scan Flag'] = 'N';
    this.normalizeProductsFromMasterHs(normalizedServiceReq);

    request.body.DCIncomingHierarchy.ListOfDCCOOMOBILE.MEMBER.CSN = normalizedServiceReq?.CSN || '';
    request.body.DCIncomingHierarchy.ListOfDCCOOMOBILE.MEMBER.ListOfSERVICEREQ.SERVICEREQ = normalizedServiceReq;

    return request;
  }

  private normalizeProductsFromMasterHs(serviceReq: any): void {
    const products = Utils.checkIsArray(serviceReq?.ListOfPRODUCT?.PRODUCT);
    if (!products.length) {
      return;
    }

    products.forEach((product: any, index: number) => {
      const hsCodeKey = (product?.['DC HS Code'] ?? '').toString().trim().toLowerCase();
      const matchedHs = hsCodeKey ? this.masterHsCodeMetaMap.get(hsCodeKey) : undefined;

      if (matchedHs) {
        product['DC Product English Name'] = matchedHs.englishName || product?.['DC Product English Name'] || '';
        product['DC Product Arabic Name'] = matchedHs.arabicName || product?.['DC Product Arabic Name'] || '';
      }

      product['DC Product Id'] = Utils.generateUniqueId();
      product['DC Seq Of Item'] = String(index + 1);
    });

    serviceReq.ListOfPRODUCT.PRODUCT = products;
  }

  private async validateRecord(record: any, index: number): Promise<string[]> {
    const errors: string[] = [];

    if (!record || typeof record !== 'object') {
      return [this.t('COO_BULK_UPLOAD.ERRORS.INVALID_OBJECT_STRUCTURE', { index })];
    }

    const rules: Array<{ field: string; type?: LookupKey; countryParentField?: string; useCurrencyList?: boolean; useYesNoList?: boolean }> = [
      { field: 'Currency', useCurrencyList: true },
      { field: 'DC Exporter Type', type: LookupKey.COO_DC_EXP_TYPE },
      { field: 'DC Consignee Type', type: LookupKey.COO_DC_CONSGN_TYPE },
      { field: 'DC Consignee Type eService', type: LookupKey.COO_DC_CONSGN_TYPE },
      { field: 'DC Country', type: LookupKey.COUNTRY },
      { field: 'DC Direct Shipment', useYesNoList: true },
      { field: 'DC Transport By', type: LookupKey.COO_DC_TRASNPORT },
      { field: 'DC Exit Point', type: LookupKey.COO_DC_COO_EXIT_POINT },
      { field: 'DC Final Destination', type: LookupKey.COUNTRY },
      { field: 'DC Port of Discharge', type: LookupKey.COO_DC_PORT_DISCHRG, countryParentField: 'DC Final Destination' },
      { field: 'DC Payment Method', type: LookupKey.PAYMENT_METHOD },
      { field: 'DC Language', type: LookupKey.COO_DC_LANG }
    ];

    const exporterNumber = (record?.['DC Exporter Number'] ?? '').toString().trim();
    const memberCsn = (record?.CSN ?? '').toString().trim();
    const invoiceDate = (record?.['DC Invoice Date'] ?? '').toString().trim();
    if (exporterNumber) {
      const isValidExporterNumber = await this.isValidExporterNumber(memberCsn, exporterNumber);
      if (!isValidExporterNumber) {
        errors.push('DC Exporter Number');
      }
    }

    if (invoiceDate && !this.isInvoiceDateWithinAllowedRange(invoiceDate)) {
      errors.push('DC Invoice Date (must be in MM/DD/YYYY format, within the past 12 months and less than or equal to today)');
    }

    for (const rule of rules) {
      const value = (record?.[rule.field] ?? '').toString().trim();
      if (!value) {
        continue;
      }

      let isValid = true;
      if (rule.useCurrencyList) {
        isValid = await this.isValidCurrency(value);
      } else if (rule.useYesNoList) {
        isValid = this.isYesNoValue(value);
      } else if (rule.type === LookupKey.COO_DC_PORT_DISCHRG) {
        const parentCountry = (record?.[rule.countryParentField || ''] ?? '').toString().trim();
        isValid = parentCountry ? await this.isValidCountryDependentLov(rule.type, parentCountry, value) : false;
      } else if (rule.type) {
        isValid = await this.isValidLov(rule.type, value);
      }

      if (!isValid) {
        errors.push(rule.field);
      }
    }

    const products = Utils.checkIsArray(record?.ListOfPRODUCT?.PRODUCT);
    for (const product of products) {
      const hsCode = (product?.['DC HS Code'] ?? '').toString().trim();
      if (hsCode && !this.isValidMasterHsCode(hsCode)) {
        errors.push('DC HS Code');
      }

      const processType = (product?.['DC Process Type'] ?? '').toString().trim();
      if (processType && !(await this.isValidLov(LookupKey.COO_DC_PROCESS_TYPE, processType))) {
        errors.push('DC Process Type');
      }

      const unit = (product?.['DC Unit of Measure'] ?? '').toString().trim();
      if (unit && !(await this.isValidLov(LookupKey.UNIT_OF_MEASURE, unit))) {
        errors.push('DC Unit of Measure');
      }

      const originCountry = (product?.['DC Country of Origin'] ?? '').toString().trim();
      if (originCountry && !(await this.isValidLov(LookupKey.COUNTRY, originCountry))) {
        errors.push('DC Country of Origin');
      }

      const processingCountry = (product?.['DC Processing Country'] ?? '').toString().trim();
      if (processingCountry && !(await this.isValidLov(LookupKey.COUNTRY, processingCountry))) {
        errors.push('DC Processing Country');
      }
    }

    return Array.from(new Set(errors));
  }

  private buildInvalidFieldMessage(fields: string[]): string {
    if (!fields.length) {
      return '';
    }
    return `Has invalid value for:\n${fields.join('\n')}`;
  }

  private isValidMasterHsCode(hsCode: string): boolean {
    if (this.masterHsCodeSet.size === 0) {
      // Do not hard-fail if preload is not yet complete/unavailable.
      return true;
    }
    return this.masterHsCodeSet.has(hsCode.toLowerCase());
  }

  private isInvoiceDateWithinAllowedRange(rawValue: string): boolean {
    const match = rawValue?.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) {
      return false;
    }

    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);

    const invoiceDate = new Date(year, month - 1, day);
    if (
      invoiceDate.getFullYear() !== year ||
      invoiceDate.getMonth() !== month - 1 ||
      invoiceDate.getDate() !== day
    ) {
      return false;
    }

    invoiceDate.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const minDate = new Date(now);
    minDate.setFullYear(now.getFullYear() - 1);

    return invoiceDate >= minDate && invoiceDate <= now;
  }

  private async isValidExporterNumber(memberCsn: string, exporterNumber: string): Promise<boolean> {
    if (!memberCsn) {
      return false;
    }

    if (!this.exporterNumberCache.has(memberCsn)) {
      const requestPayload = {
        body: {
          ProcessName: 'DC Get Exporter List WF',
          MemberNum: memberCsn,
          DCLanguage: this.sharedService.getCurrentLang() === 'ar' ? 'ARA' : 'ENU'
        }
      };

      try {
        const response: any = await firstValueFrom(
          this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.COO_GET_EXPORTER, requestPayload)
        );
        const account = response?.SiebelMessage?.Account;
        const list = Utils.checkIsArray(account?.['Party Relationship To']);
        const exporterSet = new Set<string>();

        const accountCsn = (account?.['CSN'] ?? '').toString().trim();
        if (accountCsn) {
          exporterSet.add(accountCsn.toLowerCase());
        }

        list.forEach((rel: any) => {
          const relCsn = (rel?.['DC Membership Number'] ?? '').toString().trim();
          if (relCsn) {
            exporterSet.add(relCsn.toLowerCase());
          }
        });

        this.exporterNumberCache.set(memberCsn, exporterSet);
      } catch {
        this.exporterNumberCache.set(memberCsn, new Set<string>());
      }
    }

    const validSet = this.exporterNumberCache.get(memberCsn)!;
    return validSet.has(exporterNumber.toLowerCase());
  }

  private async isValidCurrency(value: string): Promise<boolean> {
    const cacheKey = 'currency';
    if (!this.lovCache.has(cacheKey)) {
      const requestPayload = {
        body: {
          ProcessName: 'DC Get Currency List WF',
          CurrencyCode: ''
        }
      };
      const response: any = await firstValueFrom(
        this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_CURRENCY_LIST, requestPayload)
      );
      const currencies = Utils.checkIsArray(response?.SiebelMessage?.Currency);
      const list = new Set<string>();
      currencies.forEach((item: any) => {
        const code = (item?.['Currency Code'] || '').toString().trim();
        const name = (item?.Name || '').toString().trim();
        if (code) list.add(code.toLowerCase());
        if (name) list.add(name.toLowerCase());
      });
      this.lovCache.set(cacheKey, list);
    }

    const set = this.lovCache.get(cacheKey)!;
    return set.has(value.toLowerCase());
  }

  private async isValidLov(type: LookupKey, value: string): Promise<boolean> {
    if (!this.lovCache.has(type)) {
      const request = {
        body: {
          ProcessName: 'DC Get List Of Values WF',
          Type: type,
          LanguageCode: this.sharedService.getCurrentLang() === 'ar' ? 'ARA' : 'ENU',
          ParentValue: ''
        }
      };

      const response: any = await firstValueFrom(
        this.api.post(ApiRoot.DCCICPIntegration, LookupKey.LOOKUP_URL, request)
      );

      const lovList = Utils.checkIsArray(response?.SiebelMessage?.['List Of Values']);
      const values = new Set<string>();
      lovList.forEach((item: any) => {
        const lovValue = (item?.Value || '').toString().trim();
        const lovName = (item?.Name || '').toString().trim();
        if (lovValue) values.add(lovValue.toLowerCase());
        if (lovName) values.add(lovName.toLowerCase());
      });
      this.lovCache.set(type, values);
    }

    const set = this.lovCache.get(type)!;
    return set.has(value.toLowerCase());
  }

  private isYesNoValue(value: string): boolean {
    const normalized = value.toLowerCase();
    return normalized === 'yes' || normalized === 'no' || normalized === 'نعم' || normalized === 'لا';
  }

  private async isValidCountryDependentLov(type: LookupKey, parentValue: string, value: string): Promise<boolean> {
    const key = `${type}:${parentValue.toLowerCase()}`;

    if (!this.countryCache.has(key)) {
      const request = {
        body: {
          ProcessName: 'DC Get List Of Values WF',
          Type: type,
          LanguageCode: this.sharedService.getCurrentLang() === 'ar' ? 'ARA' : 'ENU',
          ParentValue: parentValue
        }
      };

      const response: any = await firstValueFrom(
        this.api.post(ApiRoot.DCCICPIntegration, LookupKey.LOOKUP_URL, request)
      );

      const lovList = Utils.checkIsArray(response?.SiebelMessage?.['List Of Values']);
      const values = new Set<string>();
      lovList.forEach((item: any) => {
        const lovValue = (item?.Value || '').toString().trim();
        const lovName = (item?.Name || '').toString().trim();
        if (lovValue) values.add(lovValue.toLowerCase());
        if (lovName) values.add(lovName.toLowerCase());
      });
      this.countryCache.set(key, values);
    }

    return this.countryCache.get(key)!.has(value.toLowerCase());
  }

  get successCount(): number {
    return this.uploadResults.filter(r => r.status === 'success').length;
  }

  get failureCount(): number {
    return this.uploadResults.filter(r => r.status !== 'success').length;
  }

  get templateUrl(): string {
    return this.sharedService.getCurrentLang() === 'ar'
      ? '/files/COO_Bulk_Upload_Template.zip'
      : '/files/COO_Bulk_Upload_Template.zip';
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translateService.instant(key, params);
  }
}
